import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ProjectPhotoImage from '@/components/base/ProjectPhotoImage';
import { type DailyLogPhoto, uploadDailyLogPhoto } from '@/hooks/useDailyLog';
import { type AssignedProject } from '@/hooks/useSubcontractorAssignments';
import { type ProjectStageSchedule } from '@/hooks/useDailyLogStageSchedule';
import { useProjectSummary } from '@/hooks/useProjectSummary';
import DailyLogReviewModal from '@/components/feature/DailyLogReviewModal';
import {
  SUBCONTRACTOR_DAILY_LOG_TEMPLATE_FIELDS,
  buildDailyLogTemplateComment,
  formatDailyLogAutoTitle,
  upsertSubcontractorDailyLog,
  linkProjectFileToDailyLog,
} from '@/lib/dailyLogUtils';

interface PendingUpload {
  id: string;
  file: File;
  previewUrl: string;
  caption: string;
  status: 'pending' | 'uploading' | 'done' | 'error';
  errorMsg?: string;
}

interface DayDetailPanelProps {
  dateKey: string;
  dateLabel: string;
  photos: DailyLogPhoto[];
  projects: AssignedProject[];
  defaultProjectId: string;
  userId: string;
  getMatchingStagesForDate: (dateKey: string, projectId: string) => ProjectStageSchedule[];
  getStageForDate: (dateKey: string, projectId: string) => ProjectStageSchedule | null;
  isNonWorkingDay?: boolean;
  nonWorkingLabel?: string;
  onClose: () => void;
  onUploadComplete: () => void;
}

const STATUS_LABEL: Record<string, { bg: string; text: string; label: string }> = {
  pending_management_review: { bg: '#FFF7ED', text: '#F97316', label: 'Pending Review' },
  approved_for_client: { bg: '#F0FDF4', text: '#059669', label: 'Approved' },
  rejected: { bg: '#FEF2F2', text: '#EF4444', label: 'Rejected' },
  internal_only: { bg: '#F3F4F6', text: '#374151', label: 'Internal' },
};

function getStatusBadge(status: string | null) {
  return STATUS_LABEL[status || ''] || { bg: '#F3F4F6', text: '#6B7280', label: status || 'Unknown' };
}

export default function DayDetailPanel({
  dateKey,
  dateLabel,
  photos,
  projects,
  defaultProjectId,
  userId,
  getMatchingStagesForDate,
  getStageForDate,
  isNonWorkingDay,
  nonWorkingLabel,
  onClose,
  onUploadComplete,
}: DayDetailPanelProps) {
  // Project selection
  const [uploadProjectId, setUploadProjectId] = useState(() => {
    if (defaultProjectId && projects.some((p) => p.id === defaultProjectId)) {
      return defaultProjectId;
    }
    return projects[0]?.id || '';
  });
  const [selectedStageId, setSelectedStageId] = useState<string>('');
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [uploadToast, setUploadToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [nonWorkToast, setNonWorkToast] = useState<string | null>(null);

  // ── Daily site note fields (9-item template) ──
  const [templateValues, setTemplateValues] = useState<string[]>(() =>
    SUBCONTRACTOR_DAILY_LOG_TEMPLATE_FIELDS.map(() => '')
  );

  // Upload state
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);
  const [showReview, setShowReview] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [cameraFileInputKey, setCameraFileInputKey] = useState(0);

  const effectiveProjectId = uploadProjectId;
  const projectName = projects.find((p) => p.id === effectiveProjectId)?.project_name || '';

  // ── LIVE project summary for the currently selected upload project ──
  const projectSummary = useProjectSummary(
    effectiveProjectId || undefined
  );

  // ── DEBUG: trace project status in Day Detail Panel ──
  useEffect(() => {
    if (!effectiveProjectId || !projectSummary.loaded) return;
    console.log('DayDetailPanel project status debug', {
      dateKey,
      projectId: effectiveProjectId,
      projectName,
      summaryProjectStatus: projectSummary.projectStatus,
      summaryActiveStage: projectSummary.activeStage,
      summaryProjectProgress: projectSummary.projectProgress,
      source: 'useProjectSummary',
      labelShown: projectSummary.projectProgress === 100 ? 'Completed (current project status)' : `Scheduled stage (historical context for ${dateKey})`,
    });
  }, [effectiveProjectId, projectSummary.loaded, projectSummary.projectStatus, projectSummary.activeStage, projectSummary.projectProgress, dateKey, projectName]);

  // Auto-detect scheduled stage for this date + project
  const matchedStages = useMemo(() => {
    if (!effectiveProjectId) return [];
    return getMatchingStagesForDate(dateKey, effectiveProjectId);
  }, [effectiveProjectId, dateKey, getMatchingStagesForDate]);

  const autoStage = useMemo(() => {
    if (!effectiveProjectId) return null;
    return getStageForDate(dateKey, effectiveProjectId);
  }, [effectiveProjectId, dateKey, getStageForDate]);

  // Sync selectedStageId to auto-detected stage whenever project or date changes
  useEffect(() => {
    if (autoStage) {
      setSelectedStageId(autoStage.id);
    } else if (matchedStages.length > 0) {
      const first = [...matchedStages].sort((a, b) => a.sort_order - b.sort_order)[0];
      setSelectedStageId(first.id);
    } else {
      setSelectedStageId('');
    }
  }, [autoStage, matchedStages]);

  const dayPhotos = useMemo(
    () => photos.filter((p) => (p.log_date || p.created_at.split('T')[0]) === dateKey),
    [photos, dateKey]
  );

  const addFiles = useCallback((files: File[]) => {
    const imageFiles = files.filter((f) => f.type.startsWith('image/'));
    const newItems: PendingUpload[] = imageFiles.map((file) => ({
      id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
      file,
      previewUrl: URL.createObjectURL(file),
      caption: '',
      status: 'pending',
    }));
    setPendingUploads((prev) => [...prev, ...newItems]);
  }, []);

  // Guard: block uploads on non-working days
  const addFilesGuarded = useCallback(
    (files: File[]) => {
      if (isNonWorkingDay) {
        setNonWorkToast(`This is a non-working day${nonWorkingLabel ? ` (${nonWorkingLabel})` : ''}.`);
        setTimeout(() => setNonWorkToast(null), 3000);
        return;
      }
      addFiles(files);
    },
    [isNonWorkingDay, nonWorkingLabel, addFiles]
  );

  const removePending = useCallback((id: string) => {
    setPendingUploads((prev) => {
      const item = prev.find((p) => p.id === id);
      if (item) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  }, []);

  const updateCaption = useCallback((id: string, caption: string) => {
    setPendingUploads((prev) => prev.map((p) => (p.id === id ? { ...p, caption } : p)));
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (isNonWorkingDay) return;
    setIsDragging(true);
  }, [isNonWorkingDay]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (!e.dataTransfer.files) return;
      addFilesGuarded(Array.from(e.dataTransfer.files));
    },
    [addFilesGuarded]
  );

  // Build combined daily note caption
  const buildCombinedCaption = (photoCaption: string): string | undefined => {
    const templateComment = buildDailyLogTemplateComment(SUBCONTRACTOR_DAILY_LOG_TEMPLATE_FIELDS, templateValues);
    const parts: string[] = [];
    if (templateComment.trim()) parts.push(templateComment);
    if (photoCaption.trim()) parts.push(`Photo: ${photoCaption.trim()}`);
    return parts.length > 0 ? parts.join(' | ') : undefined;
  };

  // Can save if project selected, stage selected (or null allowed), and there are pending uploads
  const canSave =
    !isNonWorkingDay &&
    effectiveProjectId &&
    pendingUploads.some((p) => p.status === 'pending');

  const saveDisabledReason = useMemo(() => {
    if (isNonWorkingDay) return 'Non-working day — upload disabled';
    if (!effectiveProjectId) return 'Select a project above to enable saving';
    if (!pendingUploads.some((p) => p.status === 'pending')) return 'No pending photos to save';
    return null;
  }, [isNonWorkingDay, effectiveProjectId, pendingUploads]);

  async function savePendingUploads(): Promise<boolean> {
    if (!canSave) return false;
    if (isNonWorkingDay) return false;
    setIsSaving(true);

    if (import.meta.env.DEV) {
      console.log('[DayDetailPanel] savePendingUploads effectiveProjectId:', effectiveProjectId);
    }

    // Defensive: ensure projectId is a valid UUID before attempting upload
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const cleanProjectId = effectiveProjectId.includes('::')
      ? effectiveProjectId.split('::')[0]
      : effectiveProjectId;
    if (!UUID_RE.test(cleanProjectId)) {
      setUploadToast({
        type: 'error',
        message: `Cannot save: invalid project ID "${effectiveProjectId}". Please re-select a project.`,
      });
      setIsSaving(false);
      return false;
    }

    const toUpload = pendingUploads.filter((p) => p.status === 'pending');
    let successCount = 0;
    let failCount = 0;

    const templateComment = buildDailyLogTemplateComment(
      SUBCONTRACTOR_DAILY_LOG_TEMPLATE_FIELDS,
      templateValues
    );
    const { dailyLogId, error: logError } = await upsertSubcontractorDailyLog({
      projectId: cleanProjectId,
      logDate: dateKey,
      userId,
      title: formatDailyLogAutoTitle(dateKey),
      managementComment: templateComment,
    });

    if (logError || !dailyLogId) {
      setUploadToast({
        type: 'error',
        message: logError || 'Failed to save daily log report before uploading photos.',
      });
      setIsSaving(false);
      return false;
    }

    for (const item of toUpload) {
      setPendingUploads((prev) =>
        prev.map((p) => (p.id === item.id ? { ...p, status: 'uploading' } : p))
      );

      const combinedCaption = buildCombinedCaption(item.caption);

      const { photo, error: uploadError } = await uploadDailyLogPhoto(
        item.file,
        cleanProjectId,
        userId,
        dateKey,
        { stageId: selectedStageId || null, caption: combinedCaption }
      );

      if (photo) {
        successCount++;
        await linkProjectFileToDailyLog({
          dailyLogId,
          projectId: cleanProjectId,
          fileId: photo.id,
          photoUrl: photo.file_url,
          caption: combinedCaption || null,
          uploadedBy: userId,
        });
        setPendingUploads((prev) =>
          prev.map((p) => (p.id === item.id ? { ...p, status: 'done' } : p))
        );
      } else {
        failCount++;
        setPendingUploads((prev) =>
          prev.map((p) =>
            p.id === item.id ? { ...p, status: 'error', errorMsg: uploadError || 'Upload failed' } : p
          )
        );
      }
    }

    onUploadComplete();
    setIsSaving(false);

    if (successCount > 0) {
      setUploadToast({
        type: 'success',
        message: `${successCount} photo${successCount > 1 ? 's' : ''} saved to Daily Log.`,
      });
      setTemplateValues(SUBCONTRACTOR_DAILY_LOG_TEMPLATE_FIELDS.map(() => ''));
    }
    if (failCount > 0) {
      setUploadToast({
        type: 'error',
        message: `${failCount} upload${failCount > 1 ? 's' : ''} failed.`,
      });
    }

    setTimeout(() => {
      setPendingUploads((prev) => {
        const doneIds = new Set(prev.filter((p) => p.status === 'done').map((p) => p.id));
        prev.forEach((p) => {
          if (doneIds.has(p.id)) URL.revokeObjectURL(p.previewUrl);
        });
        return prev.filter((p) => p.status !== 'done');
      });
    }, 2000);

    setTimeout(() => setUploadToast(null), 4000);

    return successCount > 0 && failCount === 0;
  }

  const hasPending = pendingUploads.length > 0;
  const pendingCount = pendingUploads.filter((p) => p.status === 'pending').length;

  const reviewBodyLines = useMemo(() => {
    const templateComment = buildDailyLogTemplateComment(
      SUBCONTRACTOR_DAILY_LOG_TEMPLATE_FIELDS,
      templateValues
    );
    return templateComment ? [templateComment] : [];
  }, [templateValues]);

  const reviewPhotos = useMemo(
    () =>
      pendingUploads
        .filter((p) => p.status === 'pending')
        .map((p) => ({
          id: p.id,
          url: p.previewUrl,
          caption: p.caption.trim() || p.file.name,
          fileName: p.file.name,
        })),
    [pendingUploads]
  );

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Panel */}
      <div className="relative bg-white w-full md:w-[640px] md:max-w-[90vw] md:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col max-h-[90vh] md:max-h-[85vh]">
        {/* Toast */}
        {uploadToast && (
          <div
            className={`absolute top-3 left-1/2 -translate-x-1/2 z-10 px-4 py-2.5 rounded-xl text-sm font-semibold shadow-lg transition-all whitespace-nowrap ${
              uploadToast.type === 'success' ? 'bg-[#059669] text-white' : 'bg-[#EF4444] text-white'
            }`}
          >
            <div className="flex items-center gap-2">
              <i className={uploadToast.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} />
              {uploadToast.message}
            </div>
          </div>
        )}
        {nonWorkToast && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 px-4 py-2.5 rounded-xl text-sm font-semibold shadow-lg transition-all whitespace-nowrap bg-[#F59E0B] text-white">
            <div className="flex items-center gap-2">
              <i className="ri-error-warning-line" />
              {nonWorkToast}
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-[#1A1A1A] font-bold text-sm">{dateLabel}</h3>
            <p className="text-[#9CA3AF] text-xs mt-0.5">
              {dayPhotos.length} photo{dayPhotos.length !== 1 ? 's' : ''} · {projectName || '—'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 cursor-pointer text-[#9CA3AF] transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Non-working day banner */}
        {isNonWorkingDay && (
          <div className="mx-5 mt-4 px-3 py-2.5 rounded-xl bg-slate-100 border border-slate-200 flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center rounded-full bg-slate-300 text-slate-500 shrink-0">
              <i className="ri-calendar-close-line text-xs" />
            </div>
            <p className="text-slate-500 text-xs font-medium">
              {nonWorkingLabel ? `${nonWorkingLabel} — ` : ''}This is a non-working day. Photo upload is disabled.
            </p>
          </div>
        )}

        {/* ── Current Project Status banner (live from useProjectSummary) ── */}
        {effectiveProjectId && projectSummary.loaded && (
          <div className="mx-5 mt-4 px-3 py-2.5 rounded-xl border flex items-center justify-between gap-3"
            style={{
              backgroundColor: projectSummary.projectProgress === 100 ? '#F0FDF4' : '#FEF0E7',
              borderColor: projectSummary.projectProgress === 100 ? '#BBF7D0' : '#FDBA74',
            }}
          >
            <div className="flex items-center gap-2 min-w-0">
              <div
                className="w-5 h-5 flex items-center justify-center rounded-full shrink-0"
                style={{
                  backgroundColor: projectSummary.projectProgress === 100 ? '#059669' : '#E8621A',
                }}
              >
                <i className="ri-check-line text-white text-xs" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold truncate" style={{ color: projectSummary.projectProgress === 100 ? '#059669' : '#E8621A' }}>
                  {projectSummary.projectProgress === 100 ? 'Current Project Status: Completed' : `Current Project Status: ${projectSummary.activeStage || projectSummary.projectStatus}`}
                </p>
                <p className="text-[10px] text-[#6B7280] mt-0.5">
                  Overall progress {projectSummary.projectProgress}% · Schedule {projectSummary.scheduleFinalProgress}% · Checklist {projectSummary.checklistFinalProgress}%
                </p>
              </div>
            </div>
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0"
              style={{
                backgroundColor: projectSummary.projectProgress === 100 ? '#F0FDF4' : '#FEF0E7',
                color: projectSummary.projectProgress === 100 ? '#059669' : '#E8621A',
              }}
            >
              {projectSummary.projectProgress}%
            </span>
          </div>
        )}

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Project selector (when multiple projects) */}
          {projects.length > 1 && (
            <div className="mb-4">
              <label className="block text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-1.5">
                Project
              </label>
              <select
                value={uploadProjectId}
                onChange={(e) => setUploadProjectId(e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 bg-white text-[#374151] focus:outline-none focus:border-[#E8621A] cursor-pointer"
              >
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.project_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Scheduled Stage display — clearly labeled as historical context for this date */}
          {effectiveProjectId && (
            <div className="mb-4">
              <label className="block text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-1.5">
                Scheduled Stage for This Date
              </label>
              {isNonWorkingDay ? (
                <div className="px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200">
                  <p className="text-slate-400 text-sm font-medium">No scheduled stage</p>
                  <p className="text-slate-400 text-[11px] mt-0.5">
                    This is a non-working day.{nonWorkingLabel ? ` (${nonWorkingLabel})` : ''}
                  </p>
                </div>
              ) : matchedStages.length === 0 ? (
                <div className="px-3 py-2.5 rounded-xl bg-gray-50 border border-gray-200">
                  <p className="text-[#6B7280] text-sm font-medium">No scheduled stage</p>
                  <p className="text-[#9CA3AF] text-[11px] mt-0.5">
                    No scheduled stage found for this date.
                  </p>
                </div>
              ) : matchedStages.length === 1 ? (
                // Single matching stage — read-only display
                <div className="px-3 py-2.5 rounded-xl bg-[#F0FDF4] border border-[#059669]/30">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#059669]" />
                    <p className="text-[#1A1A1A] text-sm font-semibold">
                      {matchedStages[0].stage_name}
                    </p>
                  </div>
                  <p className="text-[#9CA3AF] text-[11px] mt-0.5">
                    {matchedStages[0].status === 'in_progress'
                      ? 'In Progress'
                      : matchedStages[0].status === 'completed'
                        ? 'Completed'
                        : matchedStages[0].status}
                    {' · '}Scheduled for {dateKey}
                  </p>
                </div>
              ) : (
                // Multiple matching stages — limited selector
                <>
                  <select
                    value={selectedStageId}
                    onChange={(e) => setSelectedStageId(e.target.value)}
                    className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 bg-white text-[#374151] focus:outline-none focus:border-[#E8621A] cursor-pointer"
                  >
                    {matchedStages.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.stage_name}
                        {s.status === 'in_progress' ? ' (In Progress)' : s.status === 'completed' ? ' (Completed)' : ''}
                        {' — '}{s.start_date} to {s.end_date}
                      </option>
                    ))}
                  </select>
                  <p className="text-[#9CA3AF] text-[10px] mt-1">
                    Multiple stages scheduled for this date. Choose the correct one.
                  </p>
                </>
              )}
            </div>
          )}

          {/* ── Daily Work Notes ── */}
          {!isNonWorkingDay && (
            <div className="mb-5 space-y-3">
              <label className="block text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-1">
                Daily Site Report <span className="text-[#9CA3AF] font-normal normal-case">(submitted to management)</span>
              </label>

              {SUBCONTRACTOR_DAILY_LOG_TEMPLATE_FIELDS.map((label, index) => (
                <div key={label}>
                  <label className="block text-[#555555] text-xs font-semibold mb-1">
                    {index + 1}. {label}
                  </label>
                  <textarea
                    value={templateValues[index] || ''}
                    onChange={(e) => {
                      setTemplateValues((prev) => {
                        const next = [...prev];
                        next[index] = e.target.value;
                        return next;
                      });
                    }}
                    placeholder={`Enter ${label.toLowerCase()}...`}
                    rows={2}
                    maxLength={500}
                    className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 text-[#1A1A1A] placeholder-[#9CA3AF] resize-none focus:outline-none focus:border-[#E8621A]"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Upload area — hidden on non-working days */}
          {!isNonWorkingDay ? (
            <div className="mb-5">
              <label className="block text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-2">
                Upload Photos
              </label>

              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`relative border-2 border-dashed rounded-2xl p-5 text-center transition-colors ${
                  isDragging
                    ? 'border-[#059669] bg-[#059669]/5'
                    : 'border-gray-200 bg-gray-50/50 hover:bg-gray-50'
                }`}
              >
                <div className="w-10 h-10 rounded-full bg-[#059669]/10 flex items-center justify-center mx-auto mb-2">
                  <i className="ri-upload-cloud-2-line text-[#059669] text-xl" />
                </div>
                <p className="text-[#374151] text-sm font-semibold mb-1">{t('sub.stage.dragDrop')}</p>
                <p className="text-[#9CA3AF] text-xs mb-3">{t('sub.stage.orTapBelow')}</p>

                <div className="flex items-center justify-center gap-2">
                  <label className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#059669] text-white rounded-xl text-sm font-semibold cursor-pointer hover:bg-[#047857] transition-colors whitespace-nowrap">
                    <i className="ri-image-add-line text-sm" />
                    {t('sub.stage.gallery')}
                    <input
                      key={cameraFileInputKey}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files) addFilesGuarded(Array.from(e.target.files));
                        setCameraFileInputKey((k) => k + 1);
                      }}
                    />
                  </label>

                  <label className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#1A1A1A] text-white rounded-xl text-sm font-semibold cursor-pointer hover:bg-black transition-colors whitespace-nowrap">
                    <i className="ri-camera-line text-sm" />
                    {t('sub.stage.camera')}
                    <input
                      key={`cam-${cameraFileInputKey}`}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files) addFilesGuarded(Array.from(e.target.files));
                        setCameraFileInputKey((k) => k + 1);
                      }}
                    />
                  </label>
                </div>
              </div>
            </div>
          ) : (
            /* Non-working day: upload hidden, show info instead */
            <div className="mb-5 px-3 py-3 rounded-xl bg-slate-50 border border-slate-200 text-center">
              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center mx-auto mb-1.5">
                <i className="ri-upload-cloud-2-line text-slate-400 text-lg" />
              </div>
              <p className="text-slate-400 text-sm font-medium">Upload disabled</p>
              <p className="text-slate-400 text-xs mt-0.5">
                Photo upload is not available on non-working days.
              </p>
            </div>
          )}

          {/* Pending upload previews */}
          {hasPending && (
            <div className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[#374151] text-sm font-semibold">
                  Pending uploads ({pendingCount})
                </p>
                <button
                  onClick={() => {
                    pendingUploads.forEach((p) => URL.revokeObjectURL(p.previewUrl));
                    setPendingUploads([]);
                  }}
                  className="text-[#9CA3AF] text-xs font-medium hover:text-[#EF4444] cursor-pointer whitespace-nowrap"
                >
                  {t('sub.stage.clearAll')}
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
                {pendingUploads.map((item) => (
                  <div
                    key={item.id}
                    className={`border rounded-xl overflow-hidden bg-white ${
                      item.status === 'error' ? 'border-[#EF4444]' : 'border-gray-100'
                    }`}
                  >
                    <div className="relative aspect-[4/3]">
                      <img
                        src={item.previewUrl}
                        alt={item.file.name}
                        className="w-full h-full object-cover"
                      />
                      {item.status === 'uploading' && (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <i className="ri-loader-4-line animate-spin text-white text-xl" />
                        </div>
                      )}
                      {item.status === 'done' && (
                        <div className="absolute inset-0 bg-[#059669]/20 flex items-center justify-center">
                          <div className="w-8 h-8 rounded-full bg-[#059669] flex items-center justify-center">
                            <i className="ri-check-line text-white text-lg" />
                          </div>
                        </div>
                      )}
                      {item.status === 'error' && (
                        <div className="absolute inset-0 bg-[#EF4444]/20 flex items-center justify-center">
                          <div className="bg-[#FEF2F2] border border-[#EF4444] text-[#EF4444] text-xs px-2 py-1 rounded-lg">
                            {item.errorMsg || 'Failed'}
                          </div>
                        </div>
                      )}
                      <button
                        onClick={() => removePending(item.id)}
                        className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center cursor-pointer hover:bg-black/70"
                      >
                        <i className="ri-close-line text-xs" />
                      </button>
                    </div>
                    <div className="p-2">
                      <input
                        type="text"
                        placeholder="Add a caption..."
                        value={item.caption}
                        onChange={(e) => updateCaption(item.id, e.target.value)}
                        className="w-full text-xs text-[#374151] bg-gray-50 border border-gray-100 rounded-lg px-2 py-1.5 focus:outline-none focus:border-[#059669]"
                        maxLength={200}
                      />
                      <p className="text-[10px] text-[#9CA3AF] mt-1 truncate">{item.file.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Existing photos */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-[#374151] text-sm font-semibold">{t('sub.stage.photosForThisDay')}</p>
              <span className="text-[#9CA3AF] text-xs">{dayPhotos.length} total</span>
            </div>

            {dayPhotos.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
                {dayPhotos.map((photo) => {
                  const badge = getStatusBadge(photo.review_status);
                  return (
                    <div key={photo.id} className="border border-gray-100 rounded-xl overflow-hidden bg-white">
                      <div className="relative aspect-[4/3]">
                        <ProjectPhotoImage
                          src={photo.file_url}
                          alt={photo.caption || photo.file_name}
                          sizes="(max-width: 768px) 45vw, 200px"
                          loading="lazy"
                          debugContext="DayDetailPanel"
                        />
                        <div className="absolute top-2 left-2 flex gap-1">
                          <span
                            className="px-1.5 py-0.5 text-white text-[9px] font-bold rounded-full"
                            style={{ backgroundColor: badge.text }}
                          >
                            {badge.label}
                          </span>
                        </div>
                        {photo.is_client_visible && (
                          <div className="absolute top-2 right-2">
                            <span className="px-1.5 py-0.5 bg-[#059669] text-white text-[9px] font-bold rounded-full">
                              Client visible
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="p-2.5">
                        {photo.caption ? (
                          <p className="text-[11px] text-[#374151] line-clamp-2">{photo.caption}</p>
                        ) : (
                          <p className="text-[11px] text-[#9CA3AF] italic">No caption</p>
                        )}
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="text-[9px] text-[#9CA3AF]">
                            {new Date(photo.created_at).toLocaleTimeString('en-NZ', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                          {photo.stage_name && (
                            <span className="px-1.5 py-0.5 bg-[#FEF0E7] text-[#E8621A] text-[9px] font-bold rounded-full">
                              {photo.stage_name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50/50 rounded-xl">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 text-[#D1D5DB]">
                  <i className="ri-image-line text-2xl" />
                </div>
                <p className="text-[#374151] text-sm font-medium">{t('sub.stage.noPhotosYet')}</p>
                <p className="text-[#9CA3AF] text-xs mt-0.5">{t('sub.stage.uploadPhotosForDay')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer save bar */}
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between">
          <span className="text-xs text-[#9CA3AF]">
            {pendingCount > 0 ? t('sub.stage.readyToUpload', { count: pendingCount }) : t('sub.stage.noPendingUploads')}
          </span>
          <div className="flex items-center gap-2">
            {saveDisabledReason && pendingCount > 0 && (
              <span className="text-xs text-[#F59E0B] font-medium hidden sm:block">
                {saveDisabledReason}
              </span>
            )}
            <button
              onClick={() => setShowReview(true)}
              disabled={!canSave || isSaving}
              className={`px-5 py-2 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                !canSave || isSaving
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-[#E8621A] text-white hover:bg-[#D45500]'
              }`}
            >
              {isSaving ? (
                <span className="flex items-center gap-1.5">
                  <i className="ri-loader-4-line animate-spin" />
                  {t('sub.stage.saving')}
                </span>
              ) : (
                'Review Daily Log'
              )}
            </button>
          </div>
        </div>
      </div>

      <DailyLogReviewModal
        open={showReview}
        title="Review Daily Log"
        subtitle="Confirm the site report and photos before submitting to management."
        projectName={projectName}
        logDate={dateLabel}
        bodyTitle="Daily Site Report"
        bodyLines={reviewBodyLines}
        photos={reviewPhotos}
        visibilityLabel="Submitted to management (internal until PM approves for client)"
        confirmLabel="Submit to Management"
        cancelLabel="Back / Edit"
        loading={isSaving}
        onCancel={() => setShowReview(false)}
        onConfirm={async () => {
          const ok = await savePendingUploads();
          if (ok) setShowReview(false);
        }}
      />
    </div>
  );
}